import { useEffect } from "react"
import { BellIcon } from "@heroicons/react/24/outline"
import { AiOutlineClose } from "react-icons/ai" // Import the close icon
import ReactMarkdown from "react-markdown"
import { BsTrash3Fill } from "react-icons/bs"
import { useAuth0 } from "@auth0/auth0-react"
import axios from "axios"
import { useSelector, useDispatch } from "react-redux"
import { updateSchedule } from "../../features/schedule/scheduleSlice"

const NotificationSidebar = ({
	isSidebarOpen,
	toggleSidebar,
	notificationCount,
	filteredNotifications,
	fetchNotifications
}) => {
	const { getAccessTokenSilently } = useAuth0()
	const patientSchedule = useSelector(state => state.schedule.patientSchedule)
	const dispatch = useDispatch()

	const handleWithdraw = async notification => {
		console.log("Withdrawing notification:", notification)
		try {
			// Get the token
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER // Ensure the audience is correct
			})

			// Make the DELETE request using axios
			const response = await axios.delete(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notification._id}`,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			)

			// Check if the deletion was successful
			if (response.status === 200) {
				console.log(`Notification ${notification._id} deleted successfully`)

				const updatedPatientSchedule = patientSchedule.map(patient =>
					patient.patientId === notification.additionalData.patientId
						? {
								...patient,
								pendingChanges: false,
								notes: ""
							}
						: patient
				)

				console.log("Updated patient schedule:", updatedPatientSchedule)

				dispatch(updateSchedule({ patientSchedule: updatedPatientSchedule }))

				// Optionally, refresh the notification list or remove the notification from the state
				await fetchNotifications() // Fetch updated notifications and update Redux state
			} else {
				console.error("Failed to delete notification")
			}
		} catch (error) {
			console.error("Error occurred while deleting notification:", error)
		}
	}

	// Add event listener for Escape key
	useEffect(() => {
		const handleKeyDown = event => {
			if (event.key === "Escape" && isSidebarOpen) {
				toggleSidebar() // Close the sidebar when Escape is pressed
			}
		}

		// Attach the event listener
		window.addEventListener("keydown", handleKeyDown)

		// Clean up the event listener on component unmount
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
		}
	}, [isSidebarOpen, toggleSidebar])

	return (
		<>
			{/* Notification icon with count at the top right */}
			<div className="fixed top-5 right-5 flex items-center cursor-pointer">
				{isSidebarOpen ? (
					// Show close icon when the sidebar is open
					<button className="text-gray-600" onClick={toggleSidebar}>
						<AiOutlineClose className="text-xl" />
					</button>
				) : (
					// Show Bell icon and notification count when sidebar is closed
					<>
						<BellIcon
							className="text-3xl text-gray-600 cursor-pointer h-10 w-10 p-2 bg-white rounded-lg shadow-md"
							onClick={toggleSidebar} // Open sidebar when clicked
						/>
						{notificationCount > 0 && (
							<span className="ml-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
								{notificationCount}
							</span>
						)}
					</>
				)}
			</div>

			{/* Notification Sidebar */}
			<div
				className={`fixed top-0 right-0 w-80 h-full bg-white shadow-lg transition-transform z-[100] ${isSidebarOpen ? "translate-x-0" : "translate-x-full"}`}
			>
				<div className="flex justify-between items-center p-4">
					{/* Title with notification count */}
					<div className="flex items-center">
						<h2 className="text-lg text-custom-blue font-bold">Awaiting Decision</h2>
						{notificationCount > 0 && (
							<span className="ml-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
								{notificationCount}
							</span>
						)}
					</div>

					{/* Close button with X icon */}
					<button className="text-gray-600" onClick={toggleSidebar}>
						<AiOutlineClose className="text-xl" />
					</button>
				</div>

				<div className="p-4 overflow-y-auto max-h-[calc(100vh-100px)] h-full">
					{filteredNotifications.length > 0 ? (
						filteredNotifications.map((notification, index) => (
							<div key={index} className="border-b py-2 relative">
								<div className="prose prose-slate prose-sm text-black prose-li:text-black">
									<p className="font-bold">{notification.message}</p>
									<div className="font-bold mb-2">
										{notification.notification_name}
									</div>
									<ReactMarkdown>{notification.description}</ReactMarkdown>
									<div className="text-gray-400 text-sm">{notification.time}</div>
								</div>

								{/* Withdraw button positioned in the bottom-right corner */}
								<div className="flex justify-end">
									<button
										className="py-2 px-4 w-32 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2"
										onClick={() => handleWithdraw(notification)}
									>
										<BsTrash3Fill className="h-4 w-4" />
										Withdraw
									</button>
								</div>
							</div>
						))
					) : (
						<p className="text-gray-600">
							No changes awaiting decision from the charge nurse
						</p>
					)}
				</div>
			</div>
		</>
	)
}

export default NotificationSidebar
